import React, { FC, ReactNode, useEffect, useState } from 'react';
import { ReactComponent as UsersIcon } from '../../icons/users-02.svg';
import { ReactComponent as LinkIcon } from '../../icons/link-03.svg';
import { ReactComponent as ReceiptCheckIcon } from '../../icons/receipt-check.svg';
import { ReactComponent as CreditCardLockIcon } from '../../icons/credit-card-lock.svg';
import { ReactComponent as ShieldTickIcon } from '../../icons/shield-tick.svg';
import { ReactComponent as BookOpenIcon } from '../../icons/book-open-01.svg';
import { ReactComponent as CloseIcon } from '../../icons/x-close.svg';
import { OrganisationAdminTab } from './organisationTabs';
import './organisation-info-panel.scss';

interface InfoPanelContent {
  icon: ReactNode;
  lead: string;
  text: string;
}

const INFO_PANELS: Partial<Record<OrganisationAdminTab, InfoPanelContent>> = {
  members: {
    icon: <UsersIcon aria-hidden='true' />,
    lead: 'Add people',
    text: 'and grow your team. Only admins can access and change settings for your organisation.',
  },
  'connected-apps': {
    icon: <LinkIcon aria-hidden='true' />,
    lead: 'Enable connected apps for your team',
    text: 'and their secretaries. No need for them to learn anything - expensing and travel become as easy as asking.',
  },
  'information-barriers': {
    icon: <ShieldTickIcon aria-hidden='true' />,
    lead: 'Stop your secretary from discussing certain information.',
    text: 'Keep sensitive details restricted, either from all secretaries, or only for specific people.',
  },
  handbook: {
    icon: <BookOpenIcon aria-hidden='true' />,
    lead: 'Add information for your secretaries',
    text: 'about your company and policies. Share leave information, team structures, or key details so your secretaries can answer team questions.',
  },
  plan: {
    icon: <ReceiptCheckIcon aria-hidden='true' />,
    lead: 'Create and manage your subscription.',
    text: 'Change your payment option, and view or download your invoices.',
  },
  purchases: {
    icon: <CreditCardLockIcon aria-hidden='true' />,
    lead: 'Add a team card',
    text: 'and it’ll show as a payment option when your team’s secretaries make purchases. Set limits for transactions, or how much each user can spend in a month.',
  },
};

const storageKey = (tab: OrganisationAdminTab) =>
  `dream-organisation-info-dismissed-${tab}`;

const isDismissed = (tab: OrganisationAdminTab): boolean => {
  try {
    return window.localStorage.getItem(storageKey(tab)) === '1';
  } catch {
    return false;
  }
};

const markDismissed = (tab: OrganisationAdminTab) => {
  try {
    window.localStorage.setItem(storageKey(tab), '1');
  } catch {
    // Without storage the panel simply returns on the next visit.
  }
};

const FIRST_SEEN_KEY = 'dream-organisation-info-first-seen';
const INFO_PANEL_LIFETIME_MS = 7 * 24 * 60 * 60 * 1000;

const readFirstSeen = (): number | null => {
  try {
    const value = Number(window.localStorage.getItem(FIRST_SEEN_KEY));
    return value > 0 ? value : null;
  } catch {
    return null;
  }
};

const isExpired = (): boolean => {
  const firstSeen = readFirstSeen();
  return firstSeen !== null && Date.now() - firstSeen >= INFO_PANEL_LIFETIME_MS;
};

const markFirstSeen = () => {
  try {
    if (readFirstSeen() === null)
      window.localStorage.setItem(FIRST_SEEN_KEY, String(Date.now()));
  } catch {
    // Without storage the panels never expire.
  }
};

interface OrganisationInfoPanelProps {
  tab: OrganisationAdminTab;
}

/** Dismissible explainer shown above an organisation console tab (Figma 248:1498); all panels expire a week after the first is seen. */
const OrganisationInfoPanel: FC<OrganisationInfoPanelProps> = ({ tab }) => {
  const [dismissed, setDismissed] = useState<
    Partial<Record<OrganisationAdminTab, boolean>>
  >({});
  const content = INFO_PANELS[tab];
  const visible =
    !!content && !dismissed[tab] && !isDismissed(tab) && !isExpired();

  useEffect(() => {
    if (visible) markFirstSeen();
  }, [visible]);

  if (!visible) return null;

  return (
    <aside
      className='organisation-info-panel'
      data-testid='organisation-info-panel'
    >
      <span className='organisation-info-panel-icon'>{content.icon}</span>
      <p className='organisation-info-panel-text'>
        <strong>{content.lead}</strong> {content.text}
      </p>
      <button
        type='button'
        className='organisation-info-panel-close'
        aria-label='Dismiss'
        onClick={() => {
          markDismissed(tab);
          setDismissed((d) => ({ ...d, [tab]: true }));
        }}
      >
        <CloseIcon aria-hidden='true' />
      </button>
    </aside>
  );
};

export default OrganisationInfoPanel;
